/**
 * OnboardingGate - Single unified gate that handles ALL pre-app checks:
 * Welcome onboarding, Student Lost, Seller onboarding, Pending Ratings, Pending Confirmations
 * All checks run in parallel via useOnboardingGates
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboardingGates } from '@/hooks/useOnboardingGates';
import { PostLoginOnboardingModal } from '@/components/PostLoginOnboardingModal';
import { StudentLostModal } from '@/components/StudentLostModal';
import { SellerOnboardingModal } from '@/components/marketplace/SellerOnboardingModal';
import MandatoryRatingModal from '@/components/marketplace/MandatoryRatingModal';
import PendingConfirmationModal from '@/components/marketplace/PendingConfirmationModal';
import { CenteredSpinner } from '@/components/ui/CenteredSpinner';

interface OnboardingGateProps {
  children: React.ReactNode;
}

export function OnboardingGate({ children }: OnboardingGateProps) {
  const navigate = useNavigate();
  const {
    loading,
    activeModal,
    sellerPlanData,
    isSubmitting,
    handleAcceptWelcome,
    handleAcknowledgeStudentLost,
    handleAcceptSeller,
    pendingRatings,
    currentRatingIndex,
    handleRatingComplete,
    pendingConfirmation,
    handleConfirmationResolved,
  } = useOnboardingGates();

  if (loading) {
    return <CenteredSpinner fullScreen />;
  }

  if (activeModal === 'welcome') {
    return <PostLoginOnboardingModal onAccept={handleAcceptWelcome} isSubmitting={isSubmitting} />;
  }

  if (activeModal === 'student-lost') {
    return <StudentLostModal onAcknowledge={handleAcknowledgeStudentLost} isSubmitting={isSubmitting} />;
  }

  if (activeModal === 'seller') {
    return (
      <SellerOnboardingModal
        onAccept={async () => {
          await handleAcceptSeller();
          navigate('/marketplace/me');
        }}
        isSubmitting={isSubmitting}
        planLabel={sellerPlanData.planLabel}
        creditsAmount={sellerPlanData.creditsAmount}
        boostAmount={sellerPlanData.boostAmount}
        validityMonths={sellerPlanData.validityMonths}
      />
    );
  }

  if (activeModal === 'pending-confirmation' && pendingConfirmation) {
    return (
      <PendingConfirmationModal
        confirmation={pendingConfirmation}
        onResolved={handleConfirmationResolved}
      />
    );
  }

  if (activeModal === 'pending-rating' && pendingRatings.length > 0) {
    return (
      <MandatoryRatingModal
        pendingRating={pendingRatings[currentRatingIndex]}
        currentIndex={currentRatingIndex + 1}
        totalCount={pendingRatings.length}
        onComplete={handleRatingComplete}
      />
    );
  }

  return <>{children}</>;
}
